import { FC } from 'react';

import { Alert } from '@mui/material';
import { Stack } from '@mui/system';

import Metadata from '@/shared/types/metadata/index.type';

import CreateMetadataForm from '../widgets/metadata/CreateMetadataForm';
import useCreateMetadataForm from '../widgets/metadata/CreateMetadataForm/hooks/useCreateMetadataForm';
import Module from '../widgets/metadata/CreateMetadataForm/types/module.type';
import getFormFieldsToSave from '../widgets/metadata/CreateMetadataForm/utils/getFormFieldsToSave';
import ModalNotification from './ModalNotification';

type CreateNewMetadataDialogProps = {
  metadata: Metadata | null;
  module: Module;
  errors?: string[];
  onClose: () => void;
  proceed: (formList: ReturnType<typeof getFormFieldsToSave>[]) => void;
};

const CreateNewMetadataDialog: FC<CreateNewMetadataDialogProps> = ({
  metadata,
  module,
  errors,
  onClose,
  proceed,
}) => {
  const {
    forms,
    formErrors,
    onValidateForm,
    onDeleteForm,
    onAddNewForm,
    onUpdateFormValue,
    onToggleAccordion,
  } = useCreateMetadataForm({ metadata });

  if (errors?.length) {
    return (
      <ModalNotification
        size='sm'
        fullWidth
        open
        onClose={onClose}
        dialogProps={{
          title: 'Create Metadata',
          content: (
            <Stack spacing={2}>
              {errors.map((error, index) => (
                <Alert key={index} severity='error'>
                  {error}
                </Alert>
              ))}
            </Stack>
          ),
          actions: [
            {
              label: 'Close',
              onClick: onClose,
            },
          ],
        }}
      />
    );
  }

  return (
    <ModalNotification
      size='sm'
      fullWidth
      open
      onClose={forms.length === 0 ? onClose : undefined}
      dialogProps={{
        title: 'Create Metadata',
        content: metadata && (
          <CreateMetadataForm
            module={module}
            forms={forms}
            formErrors={formErrors}
            metadataTypes={metadata.type_definitions}
            onDeleteForm={onDeleteForm}
            onAddNewForm={onAddNewForm}
            onUpdateFormValue={onUpdateFormValue}
            onToggleAccordion={onToggleAccordion}
          />
        ),
        actions: [
          {
            label: 'Cancel',
            onClick: onClose,
          },
          {
            label: 'Save',
            variant: 'contained',
            onClick: () => {
              const isFormValid = onValidateForm();

              if (!isFormValid) return;

              const formToSave = forms.map((form) =>
                getFormFieldsToSave(
                  {
                    metadata_key: form.metadata_key,
                    metatype_id: form.metatype_id,
                    required_level: form.required_level,
                    metadata_category: form.metadata_category,
                    description: form.description,
                  },
                  module,
                ),
              );

              proceed(formToSave);
            },
          },
        ],
      }}
    />
  );
};

export default CreateNewMetadataDialog;
